/**
 * URL par défaut du CSV GACD hébergé sur Vercel
 */
const DEFAULT_GACD_CSV_URL = 'https://cabconnect-data.vercel.app/data/GACD_COMPLET.csv';

/**
 * Nettoie une valeur texte (équivalent de clean_text en Python)
 */
function cleanText(value) {
  if (value === null || value === undefined) {
    return null;
  }
  
  let cleaned = value.replace(/\u00A0/g, ' ').trim();
  
  // Enlever les guillemets au début et à la fin
  if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
    cleaned = cleaned.slice(1, -1).trim();
  }
  
  if (cleaned === '' || cleaned === '–' || cleaned === '—' || cleaned === '-') {
    return null;
  }
  
  return cleaned;
}

/**
 * Parse un prix depuis une chaîne (équivalent de parse_price en Python)
 */
function parsePrice(priceStr) {
  if (!priceStr) {
    return null;
  }

  let cleaned = priceStr.replace(/\u00A0/g, ' ').trim();
  if (cleaned === '' || cleaned === '–' || cleaned === '—' || cleaned === '-') {
    return null;
  }

  // Si plusieurs prix séparés par '/', prendre le premier
  if (cleaned.includes('/')) {
    cleaned = cleaned.split('/')[0].trim();
  }

  cleaned = cleaned
    .replace(/€/g, '')
    .replace(/\s/g, '')
    .replace(',', '.');

  try {
    const value = parseFloat(cleaned);
    return isNaN(value) ? null : value;
  } catch {
    return null;
  }
}

/**
 * Sépare les options multiples (équivalent de split_options en Python)
 */
function splitOptions(value) {
  if (!value) {
    return [];
  }

  // Protéger les virgules décimales (ex: DA3,5)
  const protectedValue = value.replace(/\u00A0/g, ' ').replace(/(\d),(\d)/g, '$1__DECIMAL__$2');
  
  // Séparer sur / ; | et les virgules
  const parts = protectedValue.split(/[\/;|,]+/);
  
  // Restaurer les virgules décimales et nettoyer
  return parts
    .map(part => part.replace(/__DECIMAL__/g, ',').trim())
    .filter(part => part && part.length > 0);
}

/**
 * Retourne les valeurs uniques en préservant l'ordre (équivalent de unique_preserve en Python)
 */
function uniquePreserve(values) {
  const seen = new Set();
  const result = [];
  
  for (const value of values) {
    if (!value) {
      continue;
    }
    if (!seen.has(value)) {
      seen.add(value);
      result.push(value);
    }
  }
  
  return result;
}

/**
 * Parse une ligne CSV avec délimiteur ' - ' (gère les guillemets)
 */
function parseCsvLine(line) {
  const parts = [];
  let currentPart = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
      currentPart += char;
      i += 1;
    } else if (!inQuotes && i + 3 <= line.length && line.slice(i, i + 3) === ' - ') {
      // Délimiteur trouvé en dehors des guillemets
      parts.push(currentPart.trim());
      currentPart = '';
      i += 3;
    } else {
      currentPart += char;
      i += 1;
    }
  }

  // Ajouter la dernière partie
  parts.push(currentPart.trim());
  
  return parts;
}

/**
 * Construit les produits GACD depuis les lignes CSV parsées
 */
function buildGacdProductsFromRows(rows) {
  // Grouper les lignes par référence Cabconnect (ou URL, ou nom)
  const groupedRows = new Map();

  for (const row of rows) {
    const nameValue = cleanText(row['Nom de l\'article']);
    if (!nameValue || nameValue === 'Nom de l\'article') {
      continue;
    }

    // Utiliser la référence Cabconnect comme clé de regroupement (priorité sur l'URL)
    const referenceCabconnect = cleanText(row['Référence Cabconnect']) || '';
    const url = cleanText(row['URL']) || '';
    const groupKey = referenceCabconnect || url || nameValue;

    if (!groupedRows.has(groupKey)) {
      groupedRows.set(groupKey, []);
    }
    groupedRows.get(groupKey).push(row);
  }

  const products = [];

  for (const [groupKey, groupRows] of groupedRows.entries()) {
    const base = groupRows[0];
    const nameValue = cleanText(base['Nom de l\'article']) || '';
    const brand = cleanText(base['Marque']) || '';
    const description = cleanText(base['Description']) || '';
    const category = cleanText(base['Catégorie']) || '';
    const subCategory = cleanText(base['Sous-catégorie']) || '';
    const subSubCategory = cleanText(base['Sous-sous-catégorie']) || '';
    const url = cleanText(base['URL']) || '';
    const referenceCabconnect = cleanText(base['Référence Cabconnect']);

    const variants = [];
    const colors = [];
    const sizes = [];
    const quantities = [];
    const descriptions = [];

    for (const row of groupRows) {
      const colorValue = cleanText(row['Couleur']);
      const sizeValue = cleanText(row['Taille / Dimensions']); // Ex: "XS - 5/6" (déjà complet)
      const quantityValue = cleanText(row['Quantité']); // Ex: "Boîte de 100"
      const descriptionValue = cleanText(row['Description']); // Ex: N°6 S, N°8 S, etc.
      const referenceMegadental = cleanText(row['Référence Megadental']);
      const referenceBrand = cleanText(row['Référence marque']);
      const referencePrice = parsePrice(row['Prix de référence']);
      const reducedPrice = parsePrice(row['Prix réduit']);

      variants.push({
        id: '', // Sera rempli dans finalizeProducts
        label: null,
        color: colorValue,
        size: sizeValue,
        quantity: quantityValue,
        description: descriptionValue,
        referencePrice,
        reducedPrice,
        references: {
          cabconnect: referenceCabconnect,
          supplier: referenceMegadental,
          brand: referenceBrand,
        },
      });

      if (colorValue) {
        colors.push(...splitOptions(colorValue));
      }
      if (sizeValue) {
        // Ne pas split car on veut garder "XS - 5/6" ensemble
        sizes.push(sizeValue);
      }
      if (quantityValue) {
        quantities.push(...splitOptions(quantityValue));
      }
      if (descriptionValue) {
        descriptions.push(...splitOptions(descriptionValue));
      }
    }

    products.push({
      id: '', // Sera rempli dans finalizeProducts
      name: nameValue,
      brand,
      supplier: 'GACD',
      groupReference: referenceCabconnect,
      description,
      category,
      subCategory,
      subSubCategory,
      url,
      colorOptions: uniquePreserve(colors),
      sizeOptions: uniquePreserve(sizes), // Les tailles sont déjà fusionnées
      quantityOptions: uniquePreserve(quantities),
      descriptionOptions: uniquePreserve(descriptions),
      defaultVariantId: null, // Sera rempli dans finalizeProducts
      variants,
    });
  }

  return products;
}

/**
 * Finalise les produits en assignant les IDs (équivalent de finalize_products en Python)
 */
function finalizeProducts(products) {
  for (let index = 0; index < products.length; index++) {
    const product = products[index];
    const productId = String(index + 1);
    product.id = productId;

    const variants = product.variants;
    for (let variantIndex = 0; variantIndex < variants.length; variantIndex++) {
      const variant = variants[variantIndex];
      const variantId = `${productId}-v${variantIndex + 1}`;
      variant.id = variantId;

      if (!variant.label) {
        const labelParts = [
          variant.color,
          variant.size,
          variant.quantity,
          variant.description,
        ].filter(part => part !== null);
        variant.label = labelParts.length > 0 ? labelParts.join(' / ') : null;
      }
    }

    product.defaultVariantId = variants.length > 0 ? variants[0].id : null;
  }

  return products;
}

/**
 * Charge et parse le CSV GACD depuis l'URL Vercel
 */
export async function loadGacdProductsFromCsv() {
  // Récupérer l'URL depuis la variable d'environnement ou utiliser l'URL par défaut
  let csvUrl = DEFAULT_GACD_CSV_URL;
  if (typeof process !== 'undefined' && process.env && process.env.NEXT_PUBLIC_GACD_CSV_URL) {
    csvUrl = process.env.NEXT_PUBLIC_GACD_CSV_URL;
  } else if (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__.NEXT_PUBLIC_GACD_CSV_URL) {
    csvUrl = window.__ENV__.NEXT_PUBLIC_GACD_CSV_URL;
  }

  // Fetch le CSV
  const response = await fetch(csvUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
  }

  const csvText = await response.text();
  const lines = csvText.split('\n').filter(line => line.trim().length > 0);

  if (lines.length === 0) {
    throw new Error('CSV file is empty');
  }

  // Parser l'en-tête
  const headerLine = lines[0];
  const headerParts = parseCsvLine(headerLine);
  const columns = headerParts.map(col => col.trim().replace(/^"|"$/g, ''));

  if (columns.length < 13) {
    throw new Error(`Unexpected GACD CSV schema: expected 13+ columns, got ${columns.length}`);
  }

  // Parser les lignes de données
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      continue;
    }

    let parts = parseCsvLine(line);

    // Le CSV peut avoir 16 colonnes dans les données mais seulement 15 dans l'en-tête
    // Il y a une colonne vide supplémentaire à la position 9 (entre sous-sous-catégorie et URL)
    // On doit donc la supprimer pour aligner avec l'en-tête
    if (parts.length === 16) {
      parts.splice(9, 1);
    }

    // Normaliser à 15 colonnes minimum
    while (parts.length < 15) {
      parts.push('');
    }

    // Créer le dictionnaire en mappant les parties aux colonnes
    const row = {};
    for (let j = 0; j < columns.length; j++) {
      row[columns[j]] = parts[j] || '';
    }

    rows.push(row);
  }

  // Construire les produits
  const products = buildGacdProductsFromRows(rows);

  // Finaliser les produits (assigner les IDs)
  return finalizeProducts(products);
}

